import { forwardRef, type SelectHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import { cn } from '@/lib/utils';

interface VesselSelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  /**
   * Current selection. Pass `null` for the "all vessels" sentinel
   * (when used as a filter) or for an unset form field.
   */
  value: string | null;
  onChange: (vesselId: string | null) => void;
  /**
   * Allow the empty/all option. Default true; pass false in form
   * contexts where a vessel is required (e.g. EquipmentFormPage).
   */
  allowEmpty?: boolean;
  /** Override the empty-option label (default: "All vessels"). */
  emptyLabel?: string;
}

/**
 * VesselSelect — reusable native <select> wired to useVessels().
 *
 * Used as a URL filter on EquipmentsListPage, as the vesselId form
 * field on EquipmentFormPage, and as the tree picker on F4.3.
 * Loading state disables the control silently — list pages already
 * render their own skeleton above.
 */
export const VesselSelect = forwardRef<HTMLSelectElement, VesselSelectProps>(
  ({ value, onChange, allowEmpty = true, emptyLabel, className, ...rest }, ref) => {
    const { t } = useTranslation();
    const { data, isLoading } = useVessels();

    return (
      <select
        ref={ref}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        disabled={isLoading || rest.disabled}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      >
        {allowEmpty && (
          <option value="">
            {emptyLabel ?? t('equipment.list.allVessels')}
          </option>
        )}
        {data?.map((v) => (
          <option key={v.id} value={v.id}>
            {vesselDisplayName(v)}
          </option>
        ))}
      </select>
    );
  },
);
VesselSelect.displayName = 'VesselSelect';
